/**
 * OD 报告「分类别指标 → 展开 case 明细」的纯函数（无 React / 别名依赖，便于 node --test 直测）。
 */

/** OD 业务的 task key，与后端 perception_report_service.DEFAULT_TASK 一致 */
export const OD_TASK = 'od';

/** 任务级预览图对象 key 后缀：评测桶 ``<taskId>/preview_image/preview.webp``（与任务列表同源） */
export const previewKeyOf = (taskId: number): string => `${taskId}/preview_image/preview.webp`;

/** case × 类别平铺行 → 按类别分组，保留原顺序（后端已按 case_id 排序）。 */
export function groupDetailByCategory<T extends { category: string }>(
  rows: readonly T[],
): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const r of rows) {
    const list = out.get(r.category);
    if (list) {
      list.push(r);
    } else {
      out.set(r.category, [r]);
    }
  }
  return out;
}

/**
 * 单次批量换签名 URL 的 key 上限。keys 走 GET query（逗号拼接），每个 key 编码后约 44 字符，
 * 100 个约 4.4KB，留足余量避开网关 / 服务器 8KB 量级的请求行上限（超限会 414，整批无图）。
 */
export const PREVIEW_KEY_CHUNK = 100;

/** 按固定大小切批；size 非正数时视为编程错误直接抛出，避免死循环。 */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (!Number.isInteger(size) || size <= 0) {
    throw new RangeError(`chunk size 必须为正整数，收到 ${size}`);
  }
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/** 明细行里去重后的预览图 key；taskId 为空的行无从定位，跳过。 */
export function buildPreviewKeys(rows: readonly { taskId: number | null }[]): {
  keys: string[];
  keyToTaskId: Record<string, number>;
} {
  const keyToTaskId: Record<string, number> = {};
  for (const r of rows) {
    if (r.taskId) {
      keyToTaskId[previewKeyOf(r.taskId)] = r.taskId;
    }
  }
  return { keys: Object.keys(keyToTaskId), keyToTaskId };
}
